/* eslint-disable max-lines */
import { Injectable, Injector } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { select } from '@angular-redux/store';
import {
  ColDef,
  ColumnApi,
  GridApi,
  ValueFormatterParams,
} from 'ag-grid-community';
import {
  cloneDeep,
  filter,
  find,
  forEach,
  get,
  has,
  head,
  includes,
  intersectionBy,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  reduce,
  set,
  size,
  snakeCase,
  some,
  sortBy,
  startsWith,
  toLower,
  values,
} from 'lodash';
import { BehaviorSubject, Observable, Subject, combineLatest, iif } from 'rxjs';

import {
  observableCombineLatest,
  observableIIF,
  observableOf,
  rx,
  Subscription,
} from '@shared/rx';
import { ValidationService } from '@components/form/services/validation/validation.service';
import { lazyInject } from '@containers/base/decorators';
import {
  CreateFormRules,
  EDITOR_MODE,
  ENTITY_TYPES,
  FromAndToSiteId,
  GRID_LIST_VIEW_TYPE,
  GridRowData,
  IApiListResponse,
  IBaseEntityResponse,
  IBaseEntityType,
  IBaseFormService,
  IEvent,
  IFilterQuery,
  ILimit,
  ISite,
  ITransferErrorMapper,
  ITransferFormFieldPath,
  TRANSFER_TYPE,
  ValueFormatterFn,
  XuxGridAction,
  XuxGridActionPayload,
  ISiteResponse,
  SiteForTransfer,
  MEASURE_TRANSACTION,
  IDuplicateOption,
} from '@shared/types';
import { TransferTotalFormService } from '@shared/services/transfer/transfer-totals-form.service';
import { InventoryItemVenueService } from '@app/shared/services/inventory-item-venue';
import {
  flatTreeDataByHasChildren,
  generateSpacesText,
  generateUnitTextV2,
  getDefaultMeasure,
  getObservableValue,
  getSubtractDateString,
  setGridKeyboardSupport,
  toDate,
  toMoment,
} from '@shared/utils';
import {
  TRANSFER_STATUS_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@shared/types/enums/transfer-status.enum';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import {
  BACK_END_MAX_NUMBER,
  ENTITY_ID_PATH,
  MAX_PAGE_SIZE,
  ONLY_DATE_SUBSTRING_LENGTH,
} from '@config/global-constants';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  IHistoricalQuantitiesTransfer,
  IPageJumpRouteState,
  IQohInventoryItemVenue,
  IQuantityOnHandResponse,
  InitialFactorAndCountValue,
  TooltipIconConfig,
  EntityId,
} from '@shared/types/entities';
import { DmDataService, ScanningService, STATE_KEYS } from '@shared/services';
import {
  InventoryItemVenue,
  Threshold,
} from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import { setColumnDef } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { TransferEditContainerComponent } from '@app/containers/transfer/containers';
import { DecimalNumber } from '@app/shared/common';
import {
  TRANSFER_FINAL_STATUS,
  TRANSFER_NOT_FINAL_STATUS,
} from '@shared/services/transfer/transfer.definition';
import {
  DEPARTMENT_CODE,
  DEPARTMENT_CODE_NOT_CONFIGURED,
} from '@shared/services/transfer/transfer-constants';
import { transferModalDefinition } from '@shared/services/transfer/modal.definition';
import {
  composeWithDefaultEventQuery,
  getPreEventTransactionDateRange,
  getTransferEventQueryFilter,
} from '@shared/services/transfer/transfer.query.util';
import { TransferBaseFormService } from '@shared/services/transfer-common';
import { EventsActions } from '@app/store/actions';
import { PopulateEvent } from '@app/store/reducers/events/events.reducer';

import { QohService } from '../qoh';
import { createFormRules as createTransferFormRules } from './form-rules';
import { XprtDataService } from '../xprt-data/xprt-data.service';
import {
  appendInitialFactorAndCountValue,
  calculateUnitCost,
  createPendingQuantityMap,
  findMeasureFactorValue,
  getCurrentItemQoh,
  getIsShowReceivedColStatus,
  handleTransferUnitChange,
  updateInitialFactorAndCountValue,
  getItemDmWarning,
  getItemThresholdWarning,
  getItemThresholdConfig,
  initInventoryThresholdMapper,
  qohResultOfDivFactorValue,
  getItemThreshold,
  setPrimaryAndWarehouseTopAndAddStoreNumber,
  getEventRelatedAndWarehouseSites,
  isSiteTypePrimaryWarehouse,
  getParQty,
  setParQtyColumnVisible,
  getDefaultOrSelectedUnitEntityId,
  isOpenDraftTransfer,
} from './transfer.util';
import { CompanyUtilService } from '../company/company-util.service';
import { DataSource } from '../datasource/entity-data-source.service';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.TRANSFER;

  @select(['entities', ENTITY_TYPES.TRANSFER, 'currentDocument'])
  public currentDocument$: Observable<any>;

  @lazyInject(EventsActions) public eventsActions: EventsActions;
  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(TransferTotalFormService)
  public totalFromService: TransferTotalFormService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  public listGridApi: GridApi;
  public form: FormGroup;
  protected subscriptions: Subscription[] = [];
  public createFormRulesFunction: CreateFormRules<TransferFormService> =
    createTransferFormRules;
  public inventoryItemVenueMap: IBaseEntityType = {};
  public formFieldPath: ITransferFormFieldPath = {
    isChargeable: 'is_chargeable',
    type: 'type',
    transferType: 'transfer_type',
    eventId: 'event_id',
    siteIdTo: 'site_id_to',
    siteId: 'site_id',
    siteName: 'site_name',
    siteNameTo: 'site_name_to',
    storeNumber: 'store_number',
    storeNumberTo: 'store_number_to',
    transactionDate: 'transaction_date',
    inventoryItemVenues: 'inventory_items',
    glAccountName: 'gl_account.name',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    initialCount: 'initial_count',
    count: 'count',
    itemCost: 'item_cost',
    totalCost: 'total_cost',
    category: 'category',
    quantityReversed: 'quantity_reversed',
    reversedCost: 'reversed_cost',
    receivedTotalCost: 'received_total_cost',
    status: 'status',
    departmentCodeTo: 'site_department_code_to',
    virtualTotals: 'virtual.totals',
    virtualInventoryItemVenues: 'virtual.inventory_items',
    virtualTransactionDate: 'virtual.transaction_date',
    qohFrom: 'qoh_from',
    virtualAutoPopulate: 'virtual.autoPopulate',
    isMeasureChanged: 'is_measure_changed',
    source: 'source',
    is_send: 'is_send',
  };
  public xuxGrid: XuxEditGridComponent;

  public transferDateDisable: boolean = false;
  public selectedSite: ISite;
  public eventIdDataSource: DataSource;
  public siteIdDataSource: DataSource;
  public siteIdToDataSource: DataSource;

  public isShownSubmittedColumns$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public isDisableTopSection$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public siteIdToDisabled$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public createFromHistorical: boolean = false;
  public eventLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public selectedEvent$: BehaviorSubject<IEvent> = new BehaviorSubject(null);
  public eventList$: Subject<IEvent[]> = new Subject();
  public siteList$: BehaviorSubject<ISite[]> = new BehaviorSubject([]);
  public transferStatusOptions$: BehaviorSubject<
    { text: string; value: string }[]
  > = new BehaviorSubject([]);
  // the state that from historical quantities page new transfer
  public pageJumpParameters: IPageJumpRouteState = {} as IPageJumpRouteState;
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };
  public isEventFetched: boolean = false;
  // allInventoryItems for keep all items, use for print written sheet;

  public constructor(public injector: Injector) {
    super(injector);

    this.initEventDataSource();
    this.initSiteIdAndSiteIdToDataSource();
  }

  public updateFormServiceProperty(duplicateOption?: IDuplicateOption) {
    this.isDuplicateMode = get(duplicateOption, 'isDuplicateMode', false);
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;

    this.autoPopulate();

    // check is should add submitted columns to total form
    this.isShownSubmittedColumns$.next(this.getIsShownQuantityReceivedCols());
    this.disableTopSection();
    this.setOpenStatusForm();
    this.initItemTotalsSubscription();
    this.initEventSubscription();
    this.initEventIdSubscription();
    this.initFromToSiteSubscription();
    this.initItemListSubscription();
    this.initPageParamsSubscription();
    this.initTransferDate();
    this.initFetchingQohAndQtyDataStatus();

    this.eventIdDataSource?.refresh();
    this.siteIdDataSource?.refresh();
    this.siteIdToDataSource?.refresh();
    this.createInventoryItemsMap();
    this.viewSettingToggleValue = this.getDefaultHideRowsValue();
    this.initKeyDownEventSubscription();
    this.initTransferStatusOptions();
    if (this.isDuplicateMode) {
      getFormControl(this.form, this.formFieldPath.siteId).markAsTouched();
      getFormControl(this.form, this.formFieldPath.siteIdTo).markAsTouched();
    }
  }

  public initTransferStatusOptions(): void {
    const source = getFormFieldValue(this.form, this.formFieldPath.source);
    const isSend = getFormFieldValue(this.form, this.formFieldPath.is_send);
    const status = getFormFieldValue(this.form, this.formFieldPath.status);
    if (
      isOpenDraftTransfer({
        status,
        source,
        isSend,
        i18nService: this.i18nService,
      })
    ) {
      const posTransferStatusOptions = cloneDeep(TRANSFER_STATUS_OPTIONS);
      posTransferStatusOptions.forEach((option) => {
        if (option.value === TRANSFER_STATUS_VALUE.Open) {
          option.text = I18n.Status.OpenDraftBrackets;
        }
      });
      this.transferStatusOptions$.next(posTransferStatusOptions);
    } else {
      this.transferStatusOptions$.next(TRANSFER_STATUS_OPTIONS);
    }
  }

  public destroyFormDefinition(): void {
    this.hiddenRemoveInfo$.next(true);
    this.hiddenModifiedInfo$.next(true);
    this.hiddenThresholdInfo$.next(true);
    this.isInventoryItemVenuesGridBusy$.next(false);
    this.selectedEvent$.next(null);
    this.eventLoaded$.next(false);
    this.resetTransferEffectiveDates();
    this.isEventFetched = false;
    this.isFetchingEventData$.next(false);
    this.selectedSite = null;
    this.transferStatusOptions$.next([]);

    this.scanningService.destroyServiceDefinition();
    if (this.isDuplicateMode) {
      this.uiActions.clearDuplicateOption();
      this.isDuplicateMode = false;
    }
    super.destroyFormDefinition();
  }

  public async autoPopulateForCreateNew(
    autoPopulateData: IBaseEntityType,
  ): Promise<void> {
    // only do auto populate when is create
    if (!this.isNewMode() || isEmpty(autoPopulateData)) {
      return;
    }

    // populate event, form, to, date(will populate at event change method)
    await this.populateEvent(get(autoPopulateData, this.formFieldPath.eventId));
    await this.populateSite();
  }

  public async populateSite(): Promise<void> {
    // trigger site id data source to get site, so we can trigger the auto populate logic in the siteIdAndSiteIdToDataSourceFetchList
    if (!this.isEventType()) {
      // only not event type need trigger site id(event type will trigger by event)
      await this.siteIdDataSource.nextPage();
      await this.siteIdToDataSource.nextPage();
    }
  }

  /**
   * auto populate some data
   * current will populate event and from site
   *
   * @returns promise void
   */
  public async autoPopulate(): Promise<void> {
    // only do auto populate when is create transfer and not duplicate mode
    if (!this.isNewMode() || this.isDuplicateMode) {
      return;
    }

    const autoPopulateData = this.getAutoPopulateForNewData();

    if (!isEmpty(autoPopulateData)) {
      await this.autoPopulateForCreateNew(autoPopulateData);

      return;
    }

    // update pageJumpParameters for handle historical entry
    const routeState = getObservableValue(this.routeState$);

    this.pageJumpParameters = routeState || ({} as IPageJumpRouteState);
    if (has(this.pageJumpParameters, 'historical_create_transfer')) {
      this.createFromHistorical = true;
    }

    await this.populateEvent();
    await this.populateForOnlyOneOpenEvent();
    await this.populateFromSite();
  }

  public getCachedEventId(): EntityId {
    const transferType: TRANSFER_TYPE = this.getFormFieldValue(
      this.formFieldPath.transferType,
    );

    const autoPopulateEventRecords: PopulateEvent = this.stateService.getByPath(
      STATE_KEYS.EVENT_AUTO_POPULATE_TRANSFER_EVENT,
    );

    return get(autoPopulateEventRecords, transferType);
  }

  /**
   * populate event for event type transfer
   * will trigger to get data in event id box
   *
   * @returns promise void
   */
  public async populateEvent(newEventId?: EntityId): Promise<void> {
    if (!this.isEventType()) {
      return;
    }

    const curTypePopulateEventId = isNil(newEventId)
      ? this.getCachedEventId()
      : newEventId;

    if (isEmpty(curTypePopulateEventId)) {
      return;
    }

    const events = await this.eventIdDataSource.nextPage();

    const autoPopulateEvent = find(events, { id: curTypePopulateEventId });

    if (isEmpty(autoPopulateEvent)) {
      return;
    }

    setFormFieldValue(
      this.form,
      this.formFieldPath.eventId,
      curTypePopulateEventId,
    );
  }

  public async populateForOnlyOneOpenEvent(): Promise<void> {
    const eventIdValue = getFormFieldValue(
      this.form,
      this.formFieldPath.eventId,
    );

    if (!this.isDuringEventTransfer() || !isNil(eventIdValue)) {
      return;
    }

    const eventSourceItems = getObservableValue(this.eventIdDataSource.items);

    const eventList = isEmpty(eventSourceItems)
      ? await this.eventIdDataSource.nextPage()
      : eventSourceItems;

    if (isEqual(size(eventList), 1)) {
      const eventId = get(head(eventList), 'id', '');
      setFormFieldValue(this.form, this.formFieldPath.eventId, eventId);
    }
  }

  /**
   * populate form site
   * will trigger to get data in site id box
   * and we think primary warehouse site is handle done when siteIdDataSource get data
   *
   * @returns promise void
   */
  public async populateFromSite(): Promise<void> {
    if (this.isEventType()) {
      return;
    }

    // trigger site id data source to get site, so we can trigger the auto populate logic in the siteIdAndSiteIdToDataSourceFetchList
    await this.siteIdDataSource.nextPage();
  }

  /**
   * save transfer auto populate event
   * only save when is create transfer and is event type
   *
   * @param saveDocument transfer after created
   */
  public saveTransferAutoPopulateEvent(saveDocument: IBaseEntityType): void {
    const eventId: EntityId = this.getFormFieldValue(
      this.formFieldPath.eventId,
    );

    if (
      !this.isNewMode() ||
      isEmpty(eventId) ||
      isEmpty(get(saveDocument, '_id'))
    ) {
      return;
    }

    this.eventsActions.saveTransferAutoPopulateEvent(
      eventId,
      this.getFormFieldValue(this.formFieldPath.transferType),
    );
  }

  public disableTopSection(): void {
    this.isDisableTopSection$.next(this.getIsDisableTopSection());
  }

  public getTransferDateDisable(): boolean {
    if (this.isDuplicateMode) {
      return this.transferDateDisable;
    }

    return (
      this.getIsDisableTopSection() ||
      this.isFinalStatusFun() ||
      this.transferDateDisable
    );
  }

  public getSameValueValidator(comparePath: string): ValidatorFn {
    let cachedValue = null;

    return (control: AbstractControl): ValidationErrors => {
      const controlRoot = control.root;
      const compareControl = controlRoot.get(comparePath);

      if (isNil(controlRoot.value)) return null;

      if (cachedValue !== control.value) {
        cachedValue = control.value;
        if (control.dirty) {
          compareControl?.updateValueAndValidity({
            onlySelf: true,
            emitEvent: true,
          });
        }
      }

      const isSameValue = isEqual(control.value, compareControl.value);

      return isSameValue
        ? {
            error: this.i18nService.instant(
              I18n.ErrorMessages.TransferSiteSameError,
            ),
          }
        : null;
    };
  }

  public getInventoryItemVenuesRequireValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlRoot = control.root;
      const controlParent = control.parent;

      if (isNil(controlRoot.value) || isNil(controlParent)) return null;

      const transferTypeValue: TRANSFER_TYPE =
        controlRoot.value?.[this.formFieldPath.transferType];
      const statusValue: TRANSFER_STATUS_VALUE =
        controlRoot.value?.[this.formFieldPath.status];

      // just return when form status is not open or new
      if (includes(TRANSFER_FINAL_STATUS, statusValue)) {
        return null;
      }

      const siteIdTouched = controlParent.get(
        this.formFieldPath.siteId,
      ).touched;
      const siteIdToTouched = controlParent.get(
        this.formFieldPath.siteIdTo,
      ).touched;

      if (this.isNewMode() && !(siteIdTouched && siteIdToTouched)) return null;

      const data: IQohInventoryItemVenue[] =
        this.getFlattenInventoryItemVenueData<IQohInventoryItemVenue>(
          control.value,
        );
      const hasQuantityItems = some(data, (item) => {
        const castNumber = new DecimalNumber(
          item[this.formFieldPath.initialCount],
        );
        const receivedNumber = new DecimalNumber(
          item[this.formFieldPath.count],
        );

        return getIsShowReceivedColStatus(transferTypeValue, statusValue)
          ? castNumber.gt(0) ||
              castNumber.lt(0) ||
              receivedNumber.gt(0) ||
              receivedNumber.lt(0)
          : castNumber.gt(0) || castNumber.lt(0);
      });

      return hasQuantityItems
        ? null
        : { error: I18n.ErrorMessages.TransferNoQuantityItemsError };
    };
  }

  public getEventValidator(): (
    control: AbstractControl,
  ) => Observable<ValidationErrors | null> {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (isNil(this.document)) {
        return observableOf(null);
      }

      const controlValue = control.value;
      const isFinalStatus: boolean = includes(
        TRANSFER_FINAL_STATUS,
        this.document.status,
      );
      const topSectionDisabled: boolean =
        isEqual(TRANSFER_STATUS_VALUE.Pending, this.document.status) &&
        [
          TRANSFER_TYPE.DURING_EVENT_TRANSFER,
          TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
        ].includes(this.document.transfer_type);

      return iif(
        () => isFinalStatus || topSectionDisabled || this.isEventFetched,
        observableOf(null),
        observableCombineLatest([
          this.fieldValueChanges(this.formFieldPath.eventId),
          this.eventList$,
        ]).pipe(
          rx.take(1),
          rx.map(([eventId, events]) => {
            const selectedEvent = find(events, { id: eventId });
            if (controlValue && isNil(selectedEvent)) {
              return {
                error: this.i18nService.instant(
                  I18n.InfoMessages
                    .ThePreviouslySelectedEventIsNotAvailableAnyMore,
                ),
              };
            }

            return null;
          }),
        ),
      );
    };
  }

  public initItemTotalsSubscription(): void {
    const subscription = this.totalFromService.handleTotalsSubscription(this);
    this.subscriptions.push(subscription);
  }

  public initEventSubscription(): void {
    const subscription = this.selectedEvent$
      .pipe(rx.distinctUntilChanged(), rx.filter(Boolean))
      .subscribe(this.onEventChange.bind(this));
    this.subscriptions.push(subscription);
  }

  public initFromToSiteSubscription(): void {
    const subscription = observableCombineLatest([
      this.fieldValueChanges(this.formFieldPath.siteId),
      this.fieldValueChanges(this.formFieldPath.siteIdTo),
    ])
      .pipe(rx.distinctUntilChanged(isEqual))
      .subscribe(([siteId, siteIdTo]: [string, string]): void => {
        let targetSiteIdTo = siteIdTo;
        if (!isEqual(this.selectedSite?.id, siteId)) {
          const sites = getObservableValue(this.siteIdDataSource.items);
          this.selectedSite = find(sites, (site) => isEqual(site.id, siteId));
          this.siteIdToDataSource?.refresh();
          // we need clear site to when switch site form to new primary site
          // (only in during event because of that to site is related to from site)
          if (
            this.isDuringEventTransfer() &&
            isSiteTypePrimaryWarehouse(this.selectedSite)
          ) {
            setFormFieldValue(this.form, this.formFieldPath.siteIdTo, null);
            targetSiteIdTo = null;
          }
          if (siteId) {
            this.showOrHideQohFrom(siteId, sites);
          }
        }
        this.updateDepartmentCode(siteIdTo);
        this.reopenFromToSiteSubscription();
        this.getInventoryItemVenues([siteId, targetSiteIdTo]);
      });
    this.subscriptions.push(subscription);
  }

  public showOrHideQohFrom(siteId: string, sites?: ISite[]): void {
    this.uiActions.enableLoader();
    const columnApi: ColumnApi =
      this.listGridApi?.['columnController']?.columnApi;
    columnApi?.setColumnsVisible(
      [this.formFieldPath.qohFrom],
      this.transferService.getDisplayQohFrom(siteId, sites),
    );
    this.listGridApi?.sizeColumnsToFit();
    this.uiActions.disableLoader();
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public initItemListSubscription(): void {
    const subscription = observableCombineLatest([
      this.fieldValueChanges(this.formFieldPath.inventoryItemVenues),
      this.isQohDataFetching$,
      this.isPendingQuantityFetching$,
      this.isFetchingEventData$,
    ])
      .pipe(rx.distinctUntilChanged(isEqual))
      .subscribe(
        ([
          items,
          isQohDataFetching,
          isPendingQuantityFetching,
          isExistEventEntityId,
        ]: [IQohInventoryItemVenue[], boolean, boolean, boolean]): void => {
          this.replenishMessageHidden$.next(true);
          this.replenishButtonDisabled$.next(
            (isEmpty(items) && isEmpty(this.allInventoryItems)) ||
              isQohDataFetching ||
              isPendingQuantityFetching ||
              isExistEventEntityId,
          );
          this.clearQtyRequestBtnDisable$.next(
            isEmpty(items) || !this.enableClearQtyRequestedBtn(),
          );
          this.clearQtyGroupBtnDisable$.next(
            isEmpty(items) || !this.enableClearQtyBtnGroup(),
          );
        },
      );
    this.subscriptions.push(subscription);
  }

  public initPageParamsSubscription(): void {
    this.subscriptions.push(
      this.routeState$
        .pipe(rx.debounceTime(0))
        .subscribe((state: IPageJumpRouteState) => {
          const url = this.stateService.getByPath('router');
          this.pageJumpParameters = state || ({} as IPageJumpRouteState);
          if (has(this.pageJumpParameters, 'historical_create_transfer')) {
            this.createFromHistorical = true;
            const { site_id_to, site_name_to, store_number_to } =
              this.pageJumpParameters;
            setFormFieldValue(
              this.form,
              this.formFieldPath.siteIdTo,
              site_id_to,
              true,
            );
            setFormFieldValue(
              this.form,
              this.formFieldPath.siteNameTo,
              site_name_to,
              false,
            );
            setFormFieldValue(
              this.form,
              this.formFieldPath.storeNumberTo,
              store_number_to,
              false,
            );
            setFormFieldValue(
              this.form,
              this.formFieldPath.transactionDate,
              this.companyService.getCurrentTime(),
              false,
            );
            this.siteIdToDisabled$.next(true);
          }
          if (startsWith(url, '/historical-quantities')) {
            this.siteIdToDisabled$.next(true);
          }
        }),
    );
    this.subscriptions.push(
      this.isDisableTopSection$.subscribe((res) => {
        this.siteIdToDisabled$.next(res);
      }),
    );
  }

  public initTransferDate(): void {
    const status: TRANSFER_STATUS_VALUE = this.getFormFieldValue(
      this.formFieldPath.status,
    );

    const companyCurrentDate = this.companyService.getCurrentTime();
    this.transferDateDisable = this.isOutsideOfEventTransfer();
    if (!this.isDuringEventTransfer() && status === TRANSFER_STATUS_VALUE.New) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.transactionDate,
        companyCurrentDate,
        false,
      );
    }
  }

  public initEventDataSource(): void {
    this.eventIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.EVENTS,
    );
    this.eventIdDataSource.fetchList = this.eventDataSourceFetchList.bind(this);
  }

  public initSiteIdAndSiteIdToDataSource(): void {
    this.siteIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    this.siteIdToDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (this.siteIdDataSource.service) {
      this.siteIdDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
      this.siteIdToDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
    }
    this.siteIdDataSource.fetchList = (query): Promise<IApiListResponse> =>
      this.siteIdAndSiteIdToDataSourceFetchList(
        query,
        this.formFieldPath.siteId,
      );
    this.siteIdToDataSource.fetchList = (query): Promise<IApiListResponse> =>
      this.siteIdAndSiteIdToDataSourceFetchList(
        query,
        this.formFieldPath.siteIdTo,
      );
  }

  public async siteIdAndSiteIdToDataSourceFetchList(
    query,
    formPath: ITransferFormFieldPath['siteId' | 'siteIdTo'],
    eventFieldTouched: boolean = false,
  ): Promise<IApiListResponse> {
    const isSiteIdTo = isEqual(formPath, this.formFieldPath.siteIdTo);
    const { historical_create_transfer } = this.pageJumpParameters;
    if (
      this.getIsDisableTopSection() ||
      (historical_create_transfer && isSiteIdTo)
    ) {
      if (isEqual(formPath, this.formFieldPath.siteId)) {
        const siteFromId = getFormFieldValue(
          this.form,
          this.formFieldPath.siteId,
        );
        await this.transferService.siteFromDataSourceFetchList();
        this.showOrHideQohFrom(siteFromId);
      }

      return this.preloadSiteIdDatasourceItems(isSiteIdTo);
    }

    const eventIdValue: string = getFormFieldValue(
      this.form,
      this.formFieldPath.eventId,
    );

    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `is_active eq true`,
        select: [
          'name',
          'id',
          'type',
          'business_type',
          'store_number',
          'metadata',
        ],
        filter_by_assigned_sites: true,
        include_inactive: false,
      },
      query,
      {
        top: MAX_PAGE_SIZE,
      },
    );

    if (this.isEventType()) {
      if (isNil(eventIdValue)) {
        return Promise.resolve({
          items: [],
        } as IApiListResponse<any>);
      }
      queryWithDefaults.top = MAX_PAGE_SIZE;
    }

    if (
      this.isDuringEventTransfer() &&
      isSiteTypePrimaryWarehouse(this.selectedSite) &&
      isEqual(formPath, this.formFieldPath.siteIdTo)
    ) {
      return observableCombineLatest([
        this.sitesService.fetchList(queryWithDefaults),
        this.eventLoaded$.pipe(rx.filter((loaded) => loaded)), // use this to jump over first null value
        this.fieldValueChanges(this.formFieldPath.siteId).pipe(rx.take(1)),
      ])
        .pipe(
          rx.first(),
          rx.map(([response]: [ISiteResponse, boolean, string]) => {
            const eventSites: ISite[] = get(
              this.selectedEvent,
              'available_sites',
              [],
            );
            const assignedEventSites: ISite[] = intersectionBy(
              response.items as ISite[],
              eventSites,
              'id',
            );

            return {
              ...response,
              items: assignedEventSites,
              total: size(assignedEventSites),
            };
          }),
          rx.switchMap((response) => {
            const siteIds = map(response.items, 'id');

            return this.dmDataService.getSiteGroupId(siteIds).pipe(
              rx.switchMap((siteGroupId) =>
                this.dmDataService.getSitesPreferencesBySiteGroupId(
                  siteGroupId,
                ),
              ),
              rx.map((sitePreferences) => {
                const siteFromId = getFormFieldValue(
                  this.form,
                  this.formFieldPath.siteId,
                );
                const sitePrimaryWarehouseMap = reduce(
                  sitePreferences,
                  (result, sitePreference) => {
                    if (
                      !isNil(
                        get(
                          sitePreference,
                          'preferences.back_office.warehouse',
                        ),
                      )
                    ) {
                      return {
                        ...result,
                        [sitePreference.site_id]: get(
                          sitePreference,
                          'preferences.back_office.warehouse.primary_warehouse',
                        ),
                      };
                    }

                    return result;
                  },
                  {},
                );
                if (
                  // this code may always false, need check it when refactor or change related biz
                  isEqual(formPath, this.formFieldPath.siteId)
                ) {
                  this.showOrHideQohFrom(siteFromId, response.items);
                }
                const resultSites = filter(response.items, (site) => {
                  return isEqual(
                    get(sitePrimaryWarehouseMap, site.id),
                    siteFromId,
                  );
                }) as ISite[];

                this.updateToSiteId(resultSites);

                return {
                  ...response,
                  items:
                    setPrimaryAndWarehouseTopAndAddStoreNumber(resultSites),
                  total: size(resultSites),
                };
              }),
            );
          }),
        )
        .toPromise()
        .then(
          (result) => result,
          (error) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            isSiteIdTo
              ? this.siteIdToDataSource.error.next(error)
              : this.siteIdDataSource.error.next(error);

            return Promise.reject(error);
          },
        );
    }

    return observableCombineLatest([
      this.sitesService.fetchList(queryWithDefaults),
      observableIIF(
        () => this.isEventType(),
        this.eventLoaded$.pipe(rx.filter((loaded) => loaded)), // use this to jump over first null value
        observableOf(true),
      ),
    ])
      .pipe(
        rx.first(),
        rx.map(([response]: [ISiteResponse, boolean]) => {
          this.siteList$.next(response.items as ISite[]);

          return response;
        }),
        rx.map((response: ISiteResponse) => {
          const eventSites: ISite[] = get(
            this.selectedEvent,
            'available_sites',
            [],
          );
          let sites: ISite[] = response.items as ISite[];
          const selectedFromSiteId = getFormFieldValue(
            this.form,
            this.formFieldPath.siteId,
          );
          if (isNil(this.selectedSite)) {
            this.selectedSite = find(sites, (site) =>
              isEqual(site.id, selectedFromSiteId),
            );
          }
          if (isEqual(formPath, this.formFieldPath.siteId)) {
            this.showOrHideQohFrom(selectedFromSiteId, sites);
          }
          if (this.isEventType()) {
            sites = getEventRelatedAndWarehouseSites(sites, eventSites);
            response.total = size(sites);
          }
          response.items = setPrimaryAndWarehouseTopAndAddStoreNumber(sites);

          if (isEqual(formPath, this.formFieldPath.siteId)) {
            this.updateFromSiteId(response.items, eventFieldTouched);
          }

          if (isEqual(formPath, this.formFieldPath.siteIdTo)) {
            this.updateToSiteId(response.items);
          }

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isSiteIdTo
            ? this.siteIdToDataSource.error.next(error)
            : this.siteIdDataSource.error.next(error);

          return Promise.reject(error);
        },
      );
  }

  public updateFromSiteId(
    sites: (ISite | SiteForTransfer)[],
    eventFieldTouched: boolean,
  ): void {
    if (isEmpty(sites)) {
      return;
    }

    const autoPopulateSiteId = this.getAutoPopulateForNewData({
      targetFieldPath: this.formFieldPath.siteId,
    });

    const autoPopulateStatus: {
      siteIdPopulated: boolean;
    } = this.getFormFieldValue(this.formFieldPath.virtualAutoPopulate);

    if (
      !get(autoPopulateStatus, 'siteIdPopulated', false) &&
      !isEmpty(autoPopulateSiteId)
    ) {
      const autoPopulateSite = find(sites, { id: autoPopulateSiteId });

      setFormFieldValue(this.form, this.formFieldPath.virtualAutoPopulate, {
        ...autoPopulateStatus,
        siteIdPopulated: true,
      });

      if (!isEmpty(autoPopulateSite)) {
        setFormFieldValue(
          this.form,
          this.formFieldPath.siteId,
          autoPopulateSiteId,
        );
      }
    } else {
      const headElementOfSites = head(sites as ISite[]);
      const hasPrimaryWarehouse =
        isSiteTypePrimaryWarehouse(headElementOfSites);
      if (
        hasPrimaryWarehouse &&
        !isEqual(
          this.getFormFieldValue(this.formFieldPath.siteIdTo),
          headElementOfSites.id,
        ) &&
        // event type transfer or out side new mode will auto populate
        (eventFieldTouched || (!this.isEventType() && this.isNewMode()))
      ) {
        this.selectedSite = headElementOfSites;
        setFormFieldValue(
          this.form,
          this.formFieldPath.siteId,
          get(headElementOfSites, 'id'),
        );
      }
    }
  }

  public updateToSiteId(sites: (ISite | SiteForTransfer)[]): void {
    if (isEmpty(sites)) {
      return;
    }

    const autoPopulateSiteIdTo = this.getAutoPopulateForNewData({
      targetFieldPath: this.formFieldPath.siteIdTo,
    });
    const autoPopulateSite = find(sites, { id: autoPopulateSiteIdTo });

    if (!isEmpty(autoPopulateSite)) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.siteIdTo,
        autoPopulateSiteIdTo,
      );
    }
  }

  public getPreTransferFilter(): string {
    const { historical_create_transfer, isWarehouse, site_id_to } =
      this.pageJumpParameters;
    const eventQueryFilter = getTransferEventQueryFilter(
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
    );

    if (historical_create_transfer && !isWarehouse && site_id_to) {
      return `${eventQueryFilter} and (availability_conditions/condition eq 'all' or (availability_conditions/condition eq 'include' and availability_conditions/sites eq in(${site_id_to})) or (availability_conditions/condition eq 'exclude' and availability_conditions/sites eq nin(${site_id_to})))`;
    }

    return eventQueryFilter;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public eventDataSourceFetchList(query): Promise<IApiListResponse> {
    if (!this.isEventType()) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }
    const transferTypeValue: TRANSFER_TYPE = this.getFormFieldValue(
      this.formFieldPath.transferType,
    );
    const eventId = getFormFieldValue(this.form, this.formFieldPath.eventId);
    const filterMap = {
      [TRANSFER_TYPE.PRE_EVENT_TRANSFER]: this.getPreTransferFilter(),
      [TRANSFER_TYPE.DURING_EVENT_TRANSFER]: `(status eq 'open')`,
    };
    const queryFilter = this.isFinalStatusFun()
      ? `_id eq objectid(${eventId})`
      : filterMap[transferTypeValue];
    const userQuery = {
      filter: queryFilter,
      include_inactive: this.isFinalStatusFun(),
    };
    const eventQuery = composeWithDefaultEventQuery(userQuery);
    // Request event data, disable replenish function
    this.isFetchingEventData$.next(true);

    return this.eventIdDataSource.service
      .fetchList(eventQuery)
      .pipe(
        rx.first(),
        rx.map((response) => {
          this.eventList$.next(response.items as IEvent[]);
          this.isEventFetched = true;

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.eventIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public preloadSiteIdDatasourceItems(
    isSiteIdTo: boolean,
  ): Promise<IApiListResponse> {
    const namePath = isSiteIdTo
      ? this.formFieldPath.siteNameTo
      : this.formFieldPath.siteName;
    const idPath = isSiteIdTo
      ? this.formFieldPath.siteIdTo
      : this.formFieldPath.siteId;
    const storeNumberPath = isSiteIdTo
      ? this.formFieldPath.storeNumberTo
      : this.formFieldPath.storeNumber;
    const storeNumberValue = getFormFieldValue(this.form, storeNumberPath);
    const nameValue = getFormFieldValue(this.form, namePath);
    const status = getFormFieldValue(this.form, this.formFieldPath.status);
    const idValue = getFormFieldValue(this.form, idPath);

    const listResponse = {
      items: [
        {
          // historical quantities create transfer do  siteNameTo is completed
          name:
            this.createFromHistorical &&
            isEqual(status, TRANSFER_STATUS_VALUE.New)
              ? nameValue
              : generateSpacesText(nameValue, storeNumberValue),
          id: idValue,
        },
      ],
    } as IApiListResponse<any>;

    return Promise.resolve(listResponse);
  }

  public initEventIdSubscription(): void {
    const subscription = observableCombineLatest([
      this.eventList$,
      this.fieldValueChanges(this.formFieldPath.eventId),
    ]).subscribe(([events, eventId]: [IEvent[], string]) => {
      const selectedEvent = find(events, { id: eventId });
      this.selectedEvent = selectedEvent;
      this.selectedEvent$.next(selectedEvent);
      this.eventLoaded$.next(true);
    });

    this.subscriptions.push(subscription);
  }

  public onEventChange(selectedEvent: IEvent): void {
    const eventControl = this.getFormFieldControl(this.formFieldPath.eventId);
    const salesStartDateTimeUTC = head(
      selectedEvent.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );
    const isDuringEvent = this.isDuringEventTransfer();
    const isPreEvent = this.isPreEventTypeTransfer();

    let preEventTransactionDateLimit: ILimit<string>;

    if (isPreEvent) {
      const { minimum, maximum } = getPreEventTransactionDateRange(
        companyCurrentDate,
        salesStartDate,
      );

      preEventTransactionDateLimit = {
        minimum,
        maximum,
      };

      this.transactionDateLimit.minimumObs.next(minimum);
      this.transactionDateLimit.maximumObs.next(maximum);
    }
    if (isDuringEvent) {
      this.transferDateDisable = true;
    }

    setTimeout(() => {
      // event data has been fetched, enable replenish function
      this.isFetchingEventData$.next(false);
      this.siteIdDataSource?.refresh();
      this.siteIdToDataSource?.refresh();
      // this will trigger site populate
      this.siteIdAndSiteIdToDataSourceFetchList(
        {},
        this.formFieldPath.siteId,
        eventControl.touched,
      );

      const siteIdTo = this.getAutoPopulateForNewData({
        targetFieldPath: this.formFieldPath.siteIdTo,
      });

      const autoPopulateStatus: {
        siteIdPopulated: boolean;
      } = this.getFormFieldValue(this.formFieldPath.virtualAutoPopulate);

      if (
        !get(autoPopulateStatus, 'siteIdPopulated', false) &&
        !isEmpty(siteIdTo)
      ) {
        // this will trigger site id to populate
        this.siteIdAndSiteIdToDataSourceFetchList(
          {},
          this.formFieldPath.siteIdTo,
          eventControl.touched,
        );
      }

      if (eventControl.touched) {
        setFormFieldValue(this.form, this.formFieldPath.siteId, null, false);
        if (!this.createFromHistorical) {
          // historical quantities create transfer do not clear siteIdTo
          setFormFieldValue(
            this.form,
            this.formFieldPath.siteIdTo,
            null,
            false,
          );
        }

        if (!this.isOutsideOfEventTransfer()) {
          let newTransactionDate = salesStartDate;

          // handle pre event auto populate
          if (isPreEvent) {
            newTransactionDate = companyCurrentDate;

            const transactionDate = this.getAutoPopulateForNewData<string>({
              targetFieldPath: this.formFieldPath.transactionDate,
            });

            if (
              !isEmpty(transactionDate) &&
              toMoment(transactionDate).isSameOrAfter(
                toMoment(preEventTransactionDateLimit.minimum),
              ) &&
              toMoment(transactionDate).isSameOrBefore(
                toMoment(preEventTransactionDateLimit.maximum),
              )
            ) {
              newTransactionDate = transactionDate;
            }
          }

          setFormFieldValue(
            this.form,
            this.formFieldPath.transactionDate,
            newTransactionDate,
          );
        }

        if (isDuringEvent) {
          setFormFieldValue(
            this.form,
            this.formFieldPath.virtualTransactionDate,
            salesStartDateTimeUTC,
          );
        }
      }
    });
  }

  public fillQuantityRequested(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const { action_type, historicalInventoryItems = [] } =
      this.pageJumpParameters;

    return map(items, (item: IQohInventoryItemVenue) => {
      const historicalInventoryItem = find(
        historicalInventoryItems,
        (historicalItem: IHistoricalQuantitiesTransfer) =>
          historicalItem.item_entity_id === item.entity_id,
      );
      let value = null;
      if (historicalInventoryItem) {
        value = this.calculateQuantityRequested(
          get(historicalInventoryItem, `average_${action_type}`),
          item,
        );
      }
      const diffValue =
        Number(value) >= BACK_END_MAX_NUMBER ? BACK_END_MAX_NUMBER : value;
      const initialValue = !isNil(item.initial_count)
        ? item.initial_count
        : diffValue;
      const historicalInitialTotal = !isNil(item.initial_count)
        ? undefined
        : get(historicalInventoryItem, `average_${action_type}`);

      return {
        ...item,
        ...(historicalInventoryItem
          ? {
              historical_initial_total: historicalInitialTotal,
              initial_count: initialValue,
            }
          : {}),
      };
    });
  }

  public calculateQuantityRequested(
    historicalQuantitiesValue: string,
    qohInventoryItemVenue: IQohInventoryItemVenue,
  ): string {
    const currentMeasureEntityId: string =
      qohInventoryItemVenue.measure_entity_id;
    const currentFactorValue: number = findMeasureFactorValue(
      qohInventoryItemVenue,
      currentMeasureEntityId,
    );
    if (
      isEqual(historicalQuantitiesValue, '0') ||
      Number(historicalQuantitiesValue) < 0 ||
      isEqual(currentFactorValue, 0)
    ) {
      return null;
    }
    const result = new DecimalNumber(historicalQuantitiesValue || 0).div(
      currentFactorValue,
    );

    return result.isInt() ? result.toString() : result.ceil().toString();
  }

  public filterBlankRows(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const status = getFormFieldValue(this.form, this.formFieldPath.status);
    if (
      isEqual(status, TRANSFER_STATUS_VALUE.Pending) &&
      !this.isPreEventTypeTransfer()
    ) {
      return filter(
        items,
        (item) =>
          !isNil(item[this.formFieldPath.count]) ||
          !isNil(item[this.formFieldPath.initialCount]),
      );
    }

    return filter(
      items,
      (item) => !isNil(item[this.formFieldPath.initialCount]),
    );
  }

  public updateInventoryItemVenues(
    items: IQohInventoryItemVenue[],
    markField: boolean = false,
    saveUpdatedItems: boolean = false,
    additionalOptions?: {
      forceNotFillHistorical?: boolean;
    },
  ): void {
    // historical quantities create transfer should fill the Quantity Requested
    const updatedInventoryItems: IQohInventoryItemVenue[] = map(
      this.createFromHistorical &&
        !get(additionalOptions, 'forceNotFillHistorical')
        ? this.fillQuantityRequested(items)
        : items,
      (item) => ({
        ...item,
        initialFactorAndCountValue: updateInitialFactorAndCountValue(
          item as Partial<IQohInventoryItemVenue>,
          this.isDuplicateMode,
        ),
      }),
    );
    const treeData = this.convertInventoryItemVenuesToTree(
      updatedInventoryItems,
    );
    const isFromOrToTouched = this.getFromOrToTouched();
    this.updateItemsByCompanyPreferences(
      treeData,
      this.formFieldPath.inventoryItemVenues,
      markField || isFromOrToTouched,
    );
    getFormControl(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    )?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });
    this.preserveOrUpdateAllInventoryItems(
      saveUpdatedItems,
      updatedInventoryItems,
    );

    this.isInventoryItemVenuesGridBusy$.next(false);
    setTimeout(() => {
      this.listGridApi.expandAll();
    });
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public addCountAndErrorToItems(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const cloneItems = cloneDeep(items);
    const savedInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryItemVenues,
    );
    const inventoryItemVenueQuantityMap = reduce(
      savedInventoryItemVenues,
      (itemMap, item) => {
        let initialCount = item.initial_count;
        if (this.isDuplicateMode) {
          const needChangeItem = find(
            cloneItems,
            (cloneItem) =>
              get(cloneItem, this.formFieldPath.isMeasureChanged) &&
              cloneItem.entity_id === item.item_entity_id,
          );
          if (!isNil(needChangeItem)) {
            initialCount = get(needChangeItem, 'initial_count');
          }
        }

        return {
          ...itemMap,
          [item.item_entity_id]: {
            count: item.count,
            initial_count: initialCount,
          },
        };
      },
      {},
    );
    const mapperItem = this.getUpdatedItemsLevel();

    return map(cloneItems, (item) => {
      item._id = '';
      const count = get(
        inventoryItemVenueQuantityMap,
        `${item.entity_id}.count`,
      );
      const initialCount = get(
        inventoryItemVenueQuantityMap,
        `${item.entity_id}.initial_count`,
      );
      const errors: string[] = [];

      const isDmWarningOn = mapperItem[item.entity_id]
        ? mapperItem[item.entity_id].error
        : false;
      if (isDmWarningOn) errors.push('dmWarning');

      const itemThreshold: Threshold =
        getItemThreshold(
          item,
          this.isOutsideOfEventTransfer(),
          this.selectedEvent,
        ) || null;
      const iconsInfo = {
        ...getItemThresholdConfig(itemThreshold, item.origin_measures),
        dmWarning: isDmWarningOn,
      };

      const qohTo = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteIdTo,
      );
      const qohFrom = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteId,
      );
      const currentRow = {
        count,
        iconsInfo,
        qohTo,
        qohFrom,
        initial_count: initialCount,
        _id: mapperItem[item.entity_id] ? mapperItem[item.entity_id]._id : '',
      };
      const thresholdWarning = getItemThresholdWarning({
        cellValue: iconsInfo,
        rowData: { ...item, ...currentRow },
        numberFormatter: this.numberFormatService.getNumberFormatter(false),
        i18nService: this.i18nService,
      });
      if (!isNil(thresholdWarning)) {
        errors.push('quantityRequestThresholdWarning');
        this.inventoryItemThresholdMapper[item.entity_id].error = true;
      }

      return Object.assign({}, item, {
        ...currentRow,
        error: errors,
      });
    });
  }

  /**
   * @deprecated should use getQohAndPendingQuantity in replenish utils instead
   */
  public getQohAndPendingQuantity(
    item: IQohInventoryItemVenue,
    path: string,
  ): string[] {
    const qohDataForCurrentRow = getCurrentItemQoh(
      this.qohData,
      getFormFieldValue(this.form, path),
      item,
    );
    const pendingQtyForCurrentRow = get(
      this.pendingQuantityData,
      item.entity_id,
    );

    return [
      get(qohDataForCurrentRow, this.formFieldPath.count, 0)?.toString(),
      get(pendingQtyForCurrentRow, path, 0)?.toString(),
    ];
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getIsShownQuantityReceivedCols(): boolean {
    const transferType: TRANSFER_TYPE = this.getFormFieldValue(
      this.formFieldPath.transferType,
    );
    const status = this.getFormStatus();

    return getIsShowReceivedColStatus(transferType, status);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getIsDisableTopSection(): boolean {
    const status = this.getFormStatus();
    const notFinalStatusPendingTransfer =
      !this.isPreEventTypeTransfer() &&
      isEqual(TRANSFER_STATUS_VALUE.Pending, status);

    return (
      notFinalStatusPendingTransfer ||
      includes(TRANSFER_FINAL_STATUS, status) ||
      this.isDuplicateMode
    );
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public isFinalStatusFun(): boolean {
    const statusValue = getFormFieldValue(this.form, this.formFieldPath.status);

    return some(TRANSFER_FINAL_STATUS, (status) =>
      isEqual(statusValue, status),
    );
  }

  public isEventType(): boolean {
    const transferTypeValue: TRANSFER_TYPE = !isEmpty(this.form)
      ? getFormFieldValue(this.form, this.formFieldPath.transferType)
      : null;
    const eventTypes = [
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      TRANSFER_TYPE.DURING_EVENT_TRANSFER,
    ];

    return includes(eventTypes, transferTypeValue);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public isDuringEventTransfer(): boolean {
    return this.isTransferByType(TRANSFER_TYPE.DURING_EVENT_TRANSFER);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public isOutsideOfEventTransfer(): boolean {
    return this.isTransferByType(TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getFormStatus(): TRANSFER_STATUS_VALUE {
    return this.getFormFieldValue(this.formFieldPath.status);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getQuantityCellClassRules(
    canEditStatus: TRANSFER_STATUS_VALUE[],
  ): ColDef['cellClassRules'] {
    return {
      'ag-numeric-cell': (): boolean => true,
      'show-placeholder': ({ value }): boolean => {
        const isValidValue = !(value || value === 0);
        // shown placeholder when can edit it
        if (
          isEmpty(canEditStatus) ||
          (!isEmpty(canEditStatus) &&
            includes(canEditStatus, this.getFormStatus()))
        ) {
          return isValidValue;
        }

        return false;
      },
    };
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getQohValueGetter(type: string): ValueFormatterFn {
    const getQohValueGetterFn: ValueFormatterFn = (
      params: ValueFormatterParams,
    ): string => {
      if (!params.data.isLeaf) return null;
      // in final status, we can directly get qohValue and factor value for calculating.
      // there is distinct from other status.
      if (this.isFinalStatusFun()) {
        const qohValue = new DecimalNumber(params.data[snakeCase(type)]);
        const divisionResult: number = qohValue
          .div(get(params.data, 'measure_factor_value'))
          .valueOf();
        const formattedQoh = this.numberFormatService.getNumberFormatter(false)(
          { value: divisionResult },
        );

        return formattedQoh;
      }

      return qohResultOfDivFactorValue(params, type, this.numberFormatService);
    };

    return getQohValueGetterFn;
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getIconsInfoRendererParams(): Record<
    string,
    string | boolean | object
  > {
    return {
      isBusy: observableCombineLatest([
        this.isQohDataFetching$,
        this.isPendingQuantityFetching$,
      ]).pipe(
        rx.map(
          ([isQohDataFetching, isPendingQuantityFetching]) =>
            isQohDataFetching || isPendingQuantityFetching,
        ),
      ),
      dataSource: observableCombineLatest([
        this.qohData$,
        this.pendingQuantityData$,
      ]),
      valueMapper: ({ rowData, cellValue, params }): TooltipIconConfig[] => {
        const result: TooltipIconConfig[] = [];
        const newErrorValue: string[] = [];

        if (
          !rowData.isLeaf ||
          isNil(this.form) ||
          this.isFinalStatusFun() ||
          !cellValue
        ) {
          params.node.setDataValue('error', newErrorValue);

          return result;
        }

        const { dmWarning } = cellValue;
        if (dmWarning) {
          result.push(getItemDmWarning());
          newErrorValue.push('dmWarning');
        }
        const thresholdWarning = getItemThresholdWarning({
          cellValue,
          rowData,
          numberFormatter: this.numberFormatService.getNumberFormatter(false),
          i18nService: this.i18nService,
        });
        if (!isNil(thresholdWarning)) {
          result.push(thresholdWarning);
          newErrorValue.push('quantityRequestThresholdWarning');
        }

        params.node.setDataValue('error', newErrorValue);
        if (
          this.inventoryItemThresholdMapper[rowData.entity_id].error !==
          newErrorValue.includes('quantityRequestThresholdWarning')
        ) {
          this.inventoryItemThresholdMapper[rowData.entity_id].error =
            newErrorValue.includes('quantityRequestThresholdWarning');
          this.setThresholdInfoMessage();
        }

        return result;
      },
    };
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public setThresholdInfoMessage(): void {
    const needThresholdWarningItems = filter(
      this.inventoryItemThresholdMapper,
      (item) => item.error,
    );
    this.hiddenThresholdInfo$.next(isEqual(size(needThresholdWarningItems), 0));
    if (values(needThresholdWarningItems).length > 0) {
      setTimeout(() => {
        this.thresholdInfoTitle$.next(
          this.i18nService.translate(
            I18n.InfoMessages.ThresholdWarningMessage,
            {
              count: values(needThresholdWarningItems).length,
            },
          ),
        );
      });
    }
  }

  public resetTransferEffectiveDates(): void {
    this.transferDateDisable = false;
    this.transactionDateLimit.minimumObs.next(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    );
    this.transactionDateLimit.maximumObs.next(null);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getUpdatedItemsLevel(): ITransferErrorMapper {
    const savedInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryItemVenues,
    );
    const mapper = reduce(
      savedInventoryItemVenues,
      (itemMap, item) => {
        return {
          ...itemMap,
          [item.item_entity_id]: {
            error: item.error ?? false,
            _id: item._id,
          },
        };
      },
      {},
    );

    return mapper;
  }

  public setOpenStatusForm(): void {
    if (this.mode === EDITOR_MODE.EDIT && !this.isFinalStatusFun()) {
      this.getFormFieldControl(
        this.formFieldPath.inventoryItemVenues,
      ).updateValueAndValidity();
      this.setInfoMessage();
    }
  }

  public reopenFromToSiteSubscription(): void {
    const fromFormControl = getFormControl(
      this.form,
      this.formFieldPath.siteId,
    );
    const toFormControl = getFormControl(
      this.form,
      this.formFieldPath.siteIdTo,
    );
    const eventFormControl = getFormControl(
      this.form,
      this.formFieldPath.eventId,
    );
    setTimeout(() => {
      if (
        fromFormControl?.dirty ||
        toFormControl?.dirty ||
        eventFormControl?.dirty
      ) {
        this.hiddenRemoveInfo$.next(true);
        this.hiddenModifiedInfo$.next(true);
        this.hiddenThresholdInfo$.next(true);
        setFormFieldValue(
          this.form,
          this.formFieldPath.virtualInventoryItemVenues,
          [],
        );
      }
    });
  }

  public updateDepartmentCode(siteIdTo: string): void {
    const siteToDirty = this.getFormFieldControl(
      this.formFieldPath.siteIdTo,
    ).dirty;

    // If siteIdTo field wasn't changed by the user there is no need to recalculate Department Code
    if (!siteToDirty) return null;

    const sites = this.siteList$.getValue();
    const siteToObject = find(sites, (site) => site.id === siteIdTo);

    if (isNil(siteToObject)) return null;

    const departmentCode = this.retrieveDepartmentCode(siteToObject);

    setFormFieldValue(
      this.form,
      this.formFieldPath.departmentCodeTo,
      departmentCode,
    );
  }

  public retrieveDepartmentCode(site: ISite): string {
    const siteMetadata = get(site, 'metadata', []);
    const departmentCodeMetadata = head(
      filter(siteMetadata, (item) => item.name === DEPARTMENT_CODE),
    );

    return (
      get(departmentCodeMetadata, 'value', '') || DEPARTMENT_CODE_NOT_CONFIGURED
    );
  }

  public getInventoryItemVenues([siteId, siteIdTo]: FromAndToSiteId): void {
    const isDirtyInventoryItem =
      !this.isFinalStatusFun() && this.getFromOrToTouched();

    if (isEqual(siteId, siteIdTo) || isNil(siteId) || isNil(siteIdTo)) {
      setFormFieldValue(this.form, this.formFieldPath.inventoryItemVenues, []);

      return;
    }

    setFormFieldValue(
      this.form,
      this.formFieldPath.inventoryItemVenues,
      [],
      isDirtyInventoryItem,
    );
    this.isInventoryItemVenuesGridBusy$.next(true);

    if (this.isFinalStatusFun()) {
      const inventoryItemVenues = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryItemVenues,
      );
      const inventoryItemIds = map(inventoryItemVenues, 'item_entity_id');
      this.fetchItemsUPCCodes(inventoryItemIds, inventoryItemVenues).subscribe(
        (inventoryItem) => {
          const treeData = this.getFinalStatusItemsTreeData(inventoryItem);

          this.updateItemsByCompanyPreferences(
            treeData,
            this.formFieldPath.inventoryItemVenues,
            false,
          );
          this.isQohDataFetching = false;
          this.isPendingQuantityFetching = false;
          this.isInventoryItemVenuesGridBusy$.next(false);
          if (this.isCategoryListView()) {
            setTimeout(() => {
              this.listGridApi.expandAll();
            });
          }
        },
      );
    } else {
      const query = {
        filter: 'measures/transactions/transfer eq in(true)',
        select: [
          'is_chargeable',
          'code',
          'reporting_category_major',
          'reporting_category_minor',
          'name',
          'gl_account',
          'measures',
          'vendors',
          'thresholds',
          'costs',
          'upc_codes',
        ],
      };
      iif(
        () => {
          const isOutsideOfEventTransfer = this.isOutsideOfEventTransfer();

          return (
            isOutsideOfEventTransfer ||
            (!isOutsideOfEventTransfer && this.isEventFetched)
          );
        },
        this.inventoryItemVenueService.fetchCurrentList(query, [
          siteId,
          siteIdTo,
        ]),
        combineLatest([
          this.inventoryItemVenueService.fetchCurrentList(query, [
            siteId,
            siteIdTo,
          ]),
          this.eventList$,
        ]).pipe(
          rx.take(1),
          rx.map(([res]) => res),
        ),
      )
        .pipe(
          rx.map((res: IBaseEntityResponse) => {
            return this.getInventoryItemsEntityId(res);
          }),
          rx.map((res: IBaseEntityResponse<InventoryItemVenue>) =>
            this.transferService.takeIntersectionAndProcessThreshold(
              res,
              siteIdTo,
            ),
          ),
          rx.map((res: IBaseEntityResponse) =>
            this.mixinXVIData(res.items as InventoryItemVenue[]),
          ),
          rx.mergeMap((res: IQohInventoryItemVenue[]) =>
            this.getQuantityOnHandData(res, siteId, siteIdTo).pipe(
              rx.map(() => res),
            ),
          ),
          rx.mergeMap((res) =>
            this.getPendingQuantity(siteId, siteIdTo).pipe(rx.map(() => res)),
          ),
          rx.tap(
            (res) =>
              (this.inventoryItemThresholdMapper =
                initInventoryThresholdMapper(res)),
          ),
          rx.map(this.addCountAndErrorToItems.bind(this)),
          rx.map((res: IQohInventoryItemVenue[]) =>
            appendInitialFactorAndCountValue(
              res,
              this.isDuplicateMode,
              this.inventoryItemVenueMap,
            ),
          ),
          rx.tap(() => this.setThresholdInfoMessage()),
        )
        .subscribe((items: IQohInventoryItemVenue[]) => {
          this.updateInventoryItemVenues(items, false, true);
        });
    }
  }

  public getInventoryItemsEntityId(
    res: IBaseEntityResponse,
  ): IBaseEntityResponse {
    forEach(res.items, (inventoryItem: InventoryItemVenue) => {
      const virtualInventoryItems = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryItemVenues,
      );
      const measure = find(
        virtualInventoryItems,
        (item) => item.item_entity_id === inventoryItem?.entity_id,
      );
      const measures = inventoryItem.measures || [];
      const transferMeasures = filter(
        measures,
        (item) => item.transactions.transfer,
      );
      // sort the transfer Unit by description
      const sortMeasures = sortBy(
        map(transferMeasures, (filterItem) => {
          return {
            ...filterItem,
            text: generateUnitTextV2(filterItem, {
              numberFormatService: this.numberFormatService,
              measureTransactionType: MEASURE_TRANSACTION.Transfer,
            }),
            value: filterItem.entity_id,
            description: filterItem.description,
          };
        }),
        (item) => toLower(item.description),
      );
      set(inventoryItem, 'origin_measures', measures);
      set(inventoryItem, 'measures', sortMeasures);
      // if the document have the measureEntityId use it else use first value
      const measureEntityId = get(
        measure,
        this.formFieldPath.measureEntityId,
        null,
      );
      inventoryItem[this.formFieldPath.measureEntityId] =
        getDefaultOrSelectedUnitEntityId(measureEntityId, sortMeasures);

      const parQty = getParQty(
        inventoryItem as IQohInventoryItemVenue,
        this.selectedEvent,
        this.isOutsideOfEventTransfer(),
      );
      set(inventoryItem, 'par_qty', parQty);
    });

    return res;
  }

  public createInventoryItemsMap(): void {
    const virtualInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryItemVenues,
    );
    this.inventoryItemVenueMap = reduce(
      virtualInventoryItemVenues,
      (itemMap, inventoryItemVenue) => {
        const originInitialCount = get(
          inventoryItemVenue,
          this.formFieldPath.initialCount,
        );
        const newItem = {
          ...itemMap,
          [inventoryItemVenue.item_entity_id]: {
            [this.formFieldPath.initialCount]: originInitialCount,
            item_cost: get(inventoryItemVenue, 'item_cost'),
            total_cost: get(inventoryItemVenue, 'total_cost'),
            qoh_from: get(inventoryItemVenue, 'qoh_from'),
            qoh_to: get(inventoryItemVenue, 'qoh_to'),
            [this.formFieldPath.isMeasureChanged]: get(
              inventoryItemVenue,
              this.formFieldPath.isMeasureChanged,
            ),
            origin_initial_count: originInitialCount,
            origin_measure_factor_value: get(
              inventoryItemVenue,
              this.formFieldPath.measureFactorValue,
            ),
            is_duplicated:
              this.isDuplicateMode &&
              get(inventoryItemVenue, 'is_duplicated', false),
          },
        };

        if (
          this.isDuplicateMode &&
          get(inventoryItemVenue, this.formFieldPath.isMeasureChanged)
        ) {
          const measures = filter(
            get(inventoryItemVenue, 'measures'),
            (measure) => measure.transactions.transfer,
          );

          let transferMeasure = find(measures, {
            entity_id: get(
              inventoryItemVenue,
              this.formFieldPath.measureEntityId,
            ),
          });

          if (isNil(transferMeasure)) {
            transferMeasure = getDefaultMeasure(
              measures,
              MEASURE_TRANSACTION.Transfer,
            );
          }

          const originMeasureFactor = get(
            inventoryItemVenue,
            this.formFieldPath.measureFactorValue,
          );

          const initialCount = new DecimalNumber(originInitialCount)
            .mul(originMeasureFactor)
            .div(get(transferMeasure, 'factor_value'))
            .ceil()
            .valueOf();
          set(
            newItem,
            [
              inventoryItemVenue.item_entity_id,
              this.formFieldPath.initialCount,
            ],
            initialCount,
          );

          set(
            newItem,
            [
              inventoryItemVenue.item_entity_id,
              this.formFieldPath.measureEntityId,
            ],
            get(transferMeasure, ENTITY_ID_PATH),
          );
        }

        return newItem;
      },
      {},
    );
  }

  public mixinXVIData(
    inventoryItemVenues: InventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    if (isEmpty(inventoryItemVenues)) {
      return [];
    }

    return map(inventoryItemVenues, (item): IQohInventoryItemVenue => {
      if (
        this.isDuplicateMode &&
        get(this.inventoryItemVenueMap, [
          item.entity_id,
          this.formFieldPath.isMeasureChanged,
        ])
      ) {
        set(item, this.formFieldPath.isMeasureChanged, true);
        set(
          item,
          this.formFieldPath.measureEntityId,
          get(this.inventoryItemVenueMap, [
            item.entity_id,
            this.formFieldPath.measureEntityId,
          ]),
        );
      }

      return {
        ...item,
        [this.formFieldPath.initialCount]: get(
          this.inventoryItemVenueMap,
          [item.entity_id, this.formFieldPath.initialCount],
          0,
        ),
        [this.formFieldPath.itemCost]: get(
          this.inventoryItemVenueMap,
          [item.entity_id, this.formFieldPath.itemCost],
          calculateUnitCost({ data: { ...item } }),
        ),
        [this.formFieldPath.totalCost]: get(
          this.inventoryItemVenueMap,
          [item.entity_id, this.formFieldPath.totalCost],
          0,
        ),
        qoh_from: get(this.inventoryItemVenueMap, 'qoh_from', 0),
        qoh_to: get(this.inventoryItemVenueMap, 'qoh_to', 0),
        is_duplicated: get(
          this.inventoryItemVenueMap,
          [item.entity_id, 'is_duplicated'],
          false,
        ),
      } as IQohInventoryItemVenue;
    });
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getQuantityOnHandData(
    inventoryItemVenues: IQohInventoryItemVenue[],
    siteId: string,
    siteIdTo: string,
  ): Observable<
    | IBaseEntityType
    | IQuantityOnHandResponse
    | IBaseEntityResponse<IBaseEntityType>
  > {
    if (isEmpty(inventoryItemVenues)) {
      return observableOf(null);
    }
    this.isQohDataFetching$.next(true);

    return this.transferService
      .getQuantityOnHandData(
        inventoryItemVenues,
        siteId,
        siteIdTo,
        ENTITY_ID_PATH,
      )
      .pipe(
        rx.catchError(this.catchHttpError),
        rx.tap((res) => {
          this.qohData = res.items;
          this.qohData$.next(res.items);
          this.isQohDataFetching$.next(false);
          this.transferPdfService.setQOHData(res.items);
          this.isQohDataFetching = false;
        }),
      );
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getPendingQuantity(
    siteId: string,
    siteIdTo: string,
  ): Observable<IBaseEntityResponse<IBaseEntityType>> {
    this.isPendingQuantityFetching$.next(true);

    return this.transferService
      .fetchList(
        {
          filter: `status eq 'pending' and ((site_id eq in(${siteId},${siteIdTo})) or (site_id_to eq in(${siteId},${siteIdTo})))`,
          select: [
            'site_id',
            'site_id_to',
            'inventory_items.item_entity_id',
            'inventory_items.initial_count',
            'inventory_items.measure_factor_value',
          ],
        },
        {
          skipMappers: true,
        },
        true,
      )
      .pipe(
        rx.map((res) => {
          if (!isEqual(TRANSFER_STATUS_VALUE.Pending, this.document?.status)) {
            return res;
          }
          const filteredRes = {
            ...res,
            items: filter(
              res.items,
              (transfer) => transfer._id !== this.document._id,
            ),
          };

          return filteredRes;
        }),
        rx.catchError(this.catchHttpError),
        rx.tap((res) => {
          const pendingQtyMap = createPendingQuantityMap(
            this,
            res,
            siteId,
            siteIdTo,
          );
          this.pendingQuantityData = pendingQtyMap;
          this.pendingQuantityData$.next(pendingQtyMap);
          this.isPendingQuantityFetching$.next(false);
          this.transferPdfService.setPendingQty(pendingQtyMap);
          this.isPendingQuantityFetching = false;
        }),
      );
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public defineListColumnDisplay(action: XuxGridAction): void {
    const columnApi: ColumnApi =
      action.payload?.gridApi['columnController'].columnApi;
    const gridApi: GridApi = action.payload?.gridApi;

    if (this.getIsShownQuantityReceivedCols()) {
      // shown initial request count col
      columnApi.setColumnsVisible([this.formFieldPath.count], true);

      setColumnDef(gridApi, this.formFieldPath.totalCost, {
        headerName: this.i18nService.instant(
          I18n.Forms.TransferForm.ReceivedCost,
        ),
      });
    }
    if (!this.isFinalStatusFun()) {
      this.showTransferUnitTooltips(
        gridApi,
        this.formFieldPath.measureEntityId,
      );
    }

    const siteFromId = getFormFieldValue(this.form, this.formFieldPath.siteId);
    this.showOrHideQohFrom(siteFromId);
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getGridReadyHandler(
    editor: TransferEditContainerComponent,
  ): (action: XuxGridAction, grid: XuxEditGridComponent) => void {
    return (action: XuxGridAction, grid: XuxEditGridComponent): void => {
      this.xuxGrid = grid;
      this.listGridApi = action.payload?.gridApi;
      this.scanningService.gridApi = this.listGridApi;
      this.scanningService.columnApi =
        action?.payload?.gridApi?.['columnController']?.columnApi;

      if (!editor.canEdit || this.isFinalStatusFun()) {
        // disable arrow key
        setGridKeyboardSupport(grid.xuxGridComponent, false);
      }
      const status = getFormFieldValue(this.form, this.formFieldPath.status);

      setParQtyColumnVisible(this.listGridApi, status);
      this.defineListColumnDisplay(action);
      action.payload?.gridApi.expandAll();
    };
  }

  /**
   * @deprecated Inherit TransferBaseFormService.
   */
  public getInlineChangeMapper(): (
    value: GridRowData,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (value: GridRowData, payload: XuxGridActionPayload): GridRowData => {
      let newGridRowData: GridRowData = value;
      if (!this.isFinalStatusFun()) {
        switch (payload.field) {
          case this.formFieldPath.measureEntityId:
            const updatedRowData: GridRowData = handleTransferUnitChange(
              value,
              this.selectedEvent,
              super.isOutsideOfEventTransfer(),
            );

            newGridRowData = updatedRowData;
            this.updateSavedInventoryItems(newGridRowData);
            break;
          case this.formFieldPath.count:
            const {
              count,
              receivedFactorValue,
            }: Pick<
              InitialFactorAndCountValue,
              'count' | 'receivedFactorValue'
            > = updateInitialFactorAndCountValue(value);

            newGridRowData = {
              ...value,
              // remove historicalInitialTotal value is from historical list new transfer use user type in
              historical_initial_total: undefined,
              initialFactorAndCountValue: merge(
                {},
                value.initialFactorAndCountValue,
                { count, receivedFactorValue },
              ),
            };
            this.updateSavedInventoryItems(newGridRowData);
            break;
          case this.formFieldPath.initialCount:
            const {
              initial_count,
              factorValue,
            }: Pick<
              InitialFactorAndCountValue,
              'initial_count' | 'factorValue'
            > = updateInitialFactorAndCountValue(value);

            newGridRowData = {
              ...value,
              // remove historicalInitialTotal value is from historical list new transfer use user type in
              historical_initial_total: undefined,
              initialFactorAndCountValue: merge(
                {},
                value.initialFactorAndCountValue,
                { initial_count, factorValue },
              ),
            };
            this.updateSavedInventoryItems(newGridRowData);
            break;
          default:
            newGridRowData = value;
            break;
        }
      }

      return newGridRowData;
    };
  }

  public preserveOrUpdateAllInventoryItems(
    saveUpdatedItems: boolean,
    inventoryItems: IQohInventoryItemVenue[],
  ): void {
    if (saveUpdatedItems) {
      this.allInventoryItems = inventoryItems;
      if (this.viewSettingToggleValue) {
        const filteredItems = this.filterBlankRows(this.allInventoryItems);
        this.updateInventoryItemVenues(filteredItems);
      }
    } else {
      forEach(inventoryItems, (item: IQohInventoryItemVenue) =>
        this.updateSavedInventoryItems(item),
      );
    }
  }

  public updateSavedInventoryItems(
    updateItem: IQohInventoryItemVenue | GridRowData,
  ): void {
    this.allInventoryItems = map(this.allInventoryItems, (item) => {
      if (isEqual(item.entity_id, updateItem.entity_id)) {
        return { ...item, ...updateItem };
      }

      return item;
    });
  }

  public getFromOrToTouched(): boolean {
    const fromTouched = this.getFormFieldControl(
      this.formFieldPath.siteId,
    ).touched;
    const toTouched = this.getFormFieldControl(
      this.formFieldPath.siteIdTo,
    ).touched;

    return fromTouched || toTouched;
  }

  /**
   * getDifferentStatusHiddenValue replace old method getClickReplenishToPARHidden;
   * use for ViewSetting and ReplenishToPAR to display or hidden;
   */
  public getDifferentStatusHiddenValue(): boolean {
    const status: TRANSFER_STATUS_VALUE = this.getFormFieldValue(
      this.formFieldPath.status,
    );

    return !includes(TRANSFER_NOT_FINAL_STATUS, status);
  }

  public isListView(): boolean {
    return isEqual(
      this.gridListView,
      GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW,
    );
  }

  public async openViewSettingModal(): Promise<void> {
    const modalOptions = transferModalDefinition.viewSettingModal(
      this.viewSettingToggleValue,
    );
    const modalResult = await this.uiActions.promptFormModal(modalOptions);
    if (!modalResult.confirmed) return;
    this.viewSettingToggleValue = get(modalResult, 'value.hide_empty_rows');
    const currentItems = getFormFieldValue(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    );

    if (this.viewSettingToggleValue) {
      let filteredItems;
      let flattenItems = currentItems;
      if (this.isListView()) {
        filteredItems = this.filterBlankRows(flattenItems);
      } else {
        flattenItems = flatTreeDataByHasChildren(currentItems);
        filteredItems = this.filterBlankRows(flattenItems);
      }

      this.updateInventoryItemVenues(filteredItems, false, false, {
        forceNotFillHistorical: true,
      });
    } else {
      let mergedItems = cloneDeep(this.allInventoryItems);
      if (this.isListView()) {
        mergedItems = sortBy(mergedItems, (item) => toLower(item?.name));
      }
      this.updateInventoryItemVenues(mergedItems, false, false, {
        forceNotFillHistorical: true,
      });
    }
  }

  public getDefaultHideRowsValue(): boolean {
    const transferStatus: TRANSFER_STATUS_VALUE = this.getFormStatus();
    const isToggleOn: boolean = isEqual(
      TRANSFER_STATUS_VALUE.Pending,
      transferStatus,
    );

    return isToggleOn;
  }

  public canPrintEmptyPdf(): boolean {
    const status = this.getFormStatus();

    return includes(
      [TRANSFER_STATUS_VALUE.Pending, TRANSFER_STATUS_VALUE.Open],
      status,
    );
  }

  public afterClearQtyConfirmed(
    inventoryItems: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    if (has(this.pageJumpParameters, 'historical_create_transfer')) {
      return map(inventoryItems, (item) => ({
        ...item,
        historical_initial_total: null,
      }));
    }

    return super.afterClearQtyConfirmed(inventoryItems);
  }

  public initKeyDownEventSubscription(): void {
    this.scanningService.scanOptions = {
      isCategoryListView: this.isCategoryListView(),
      focusKeyForListModal: this.formFieldPath.initialCount,
      focusKeyForTreeModal: this.formFieldPath.initialCount,
      isCompleted: this.isFinalStatusFun(),
      focusOnNextRow: false,
      customEntityIdPath: 'item_entity_id',
    };
    const scanningSubscription =
      this.scanningService.returnScanningSubscription(
        this.scanningService.scanOptions,
      );
    this.subscriptions.push(scanningSubscription);
  }

  private initFetchingQohAndQtyDataStatus(): void {
    this.isPendingQuantityFetching = true;
    this.isQohDataFetching = true;
  }
  // eslint-disable-next-line max-lines
}
